import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { Indicator } from '@bumble/app/components/Indicator';
import CreateModal from '@bumble/app/components/CreateModal';
import DialogStandard from '@bumble/app/components/DialogStandard';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { DataPoolCreate, DataPoolEdit } from '@bumble/app/components/shared-data-pool';
import { apiError } from '@bumble/app/utils/api-error';

const columns: GridColDef[] = [
  {
    field: 'data_pool_name',
    headerName: 'Name',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'entity_name',
    headerName: 'Entity',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'usage_exceeded',
    headerName: 'Usage Exceeded',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'throttled',
    headerName: 'CPE Throttled',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'carrier',
    headerName: 'Carrier',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'data_allowance_mb',
    headerName: 'Monthly Allowance Per CPE',
    width: 220,
    disableColumnMenu: true,
    valueFormatter: (value) => (value != null ? `${(value / 1024).toFixed(2)} GB` : ''),
  },
  {
    field: 'num_cpes',
    headerName: 'Num of CPEs',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'monthly_data_usage_gb',
    headerName: 'Monthly Data Usage (GB)',
    width: 240,
    disableColumnMenu: true,
  },
  {
    field: 'percent_left',
    headerName: '% Left',
    width: 120,
    disableColumnMenu: true,
    valueFormatter: (value) => (value != null ? `${value.toFixed(1)}%` : ''),
  },
  {
    field: 'data_remaining_gb',
    headerName: 'GB Left',
    width: 120,
    disableColumnMenu: true,
    valueFormatter: (value) => (value != null ? `${value.toFixed(2)} GB` : ''),
  },
  {
    field: 'alert_threshold_percent',
    headerName: 'Alert Threshold (%)',
    width: 180,
    disableColumnMenu: true,
    renderCell: (params) => {
      return <div>{params.row.alert_enable && params.row.alert_threshold_percent != null ? `${params.row.alert_threshold_percent}%` : ''}</div>;
    },
  },
  {
    field: 'throttle_throughput_mbps',
    headerName: 'Throttled Throughput (mbps)',
    width: 240,
    disableColumnMenu: true,
    renderCell: (params) => {
      return <div>{params.row.throttle_enable && params.row.throttle_throughput_mbps != null ? `${params.row.throttle_throughput_mbps}` : ''}</div>;
    },
  },
  {
    field: 'reset_day',
    headerName: 'Reset Day',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'days_left_in_cycle',
    headerName: 'Days left in cycle',
    width: 160,
    disableColumnMenu: true,
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
];

const memberColumns: GridColDef[] = [
  {
    field: 'cpe_name',
    headerName: 'CPE Name',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'site_address',
    headerName: 'Site Address',
    flex: 1,
    minWidth: 250,
    disableColumnMenu: true,
  },
  {
    field: 'monthly_data_usage_gb',
    headerName: 'Monthly Data Usage (GB)',
    width: 1,
    minWidth: 240,
    disableColumnMenu: true,
  },
  {
    field: 'provider_service_id',
    headerName: 'Service Provider ID',
    width: 180,
    disableColumnMenu: true,
  },
  {
    field: 'client_circuit_id',
    headerName: 'Client Circuit ID',
    width: 180,
    disableColumnMenu: true,
  },
  {
    field: 'op_state',
    headerName: 'Op State',
    width: 150,
    disableColumnMenu: true,
    renderCell: (params) => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        {params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A'}
      </Box>
    ),
  },
  {
    field: 'active_uplink',
    headerName: 'Active WAN',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'cpe_id',
    headerName: 'Device ID',
    width: 200,
    disableColumnMenu: true,
  },
];

export const SharedDataPool = () => {
  const api = useApi();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const { isLoading, data, refetch, isRefetching } = useQuery(
    ['dataPools'],
    api.getDataPoolApiV1GetDataPoolGet.bind(null, {}),
    { enabled: true },
  );

  const { data: entitiesData } = useQuery(
    ['entities'],
    api.getEntityApiV1GetEntityGet.bind(null, {}),
    { enabled: true },
  );

  const removeDataPool = useMutation(api.removeDataPoolApiV1RemoveDataPoolPost, {
    onSuccess() {
      toast.success('Shared Data Pool deleted successfully.');
      setRowSelectionModel([]);
      refetch();
    },
    onError: apiError,
  });

  const entityNameById = useMemo(() => {
    const map = new Map<string, string>();
    entitiesData?.data.forEach((entity) => {
      map.set(entity.entity_id, entity.entity_name);
    });
    return map;
  }, [entitiesData]);

  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  const selectedDataPool = useMemo(() => {
    if (!selectedRowItem) return undefined;
    return data?.data.find((pool) => pool.data_pool_id === selectedRowItem);
  }, [data, selectedRowItem]);

  const {
    data: memberData,
    isLoading: loadingMembers,
    isRefetching: refetchingMembers,
    refetch: refetchMembers,
  } = useQuery(
    ['dataPoolCpes', selectedRowItem],
    () => api.getDataPoolCpesApiV1GetDataPoolCpesGet({ data_pool_id: selectedRowItem }),
    { enabled: !!selectedRowItem },
  );

  const memberRows = useMemo(() => {
    return (
      memberData?.data.map((cpe) => ({
        ...cpe,
        id: cpe.cpe_id,
      })) || []
    );
  }, [memberData]);

  const rows = useMemo(() => {
    return (
      data?.data.map((pool) => ({
        ...pool,
        id: pool.data_pool_id,
        entity_name: entityNameById.get(pool.entity_id) ?? pool.entity_id,
      })) || []
    );
  }, [data, entityNameById]);

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed Shared Data Pools.');
  };

  const handleCreateClose = () => {
    setShowCreateModal(false);
    refetch();
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    refetch();
  };

  const handleDelete = () => {
    if (!selectedDataPool) return;
    removeDataPool.mutate({
      data_pool_id: selectedDataPool.data_pool_id,
    });
  };

  return (
    <PageContent>
      <PageHeader
        pageName="Shared Data Pool"
        count={data?.data?.length || 0}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <Card elevation={0}>
          <CardContent>
            <DataTableGrid
              rows={rows}
              columns={columns}
              rowSelectionModel={rowSelectionModel}
              handleSelectionChange={handleSelectionChange}
              itemsSelected={rowSelectionModel.length === 1}
              newLabel="Create"
              newOnClick={() => setShowCreateModal(true)}
              refreshData={handleDataRefetch}
              isRefetching={isRefetching}
              menuItems={[
                {
                  label: 'Edit Shared Data Pool',
                  icon: <EditIcon color="info" fontSize="small" />,
                  onClick: () => setShowEditModal(true),
                },
                {
                  label: 'Delete Shared Data Pool',
                  icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                  onClick: () => setShowConfirmDelete(true),
                  hasDivider: true,
                },
              ]}
            />
          </CardContent>
        </Card>
      )}
      {selectedDataPool && (
        <Card elevation={0} sx={{ mt: 5 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Members of {selectedDataPool.data_pool_name}
            </Typography>
            {loadingMembers ? (
              <Loading />
            ) : (
              <DataTableGrid
                rows={memberRows}
                columns={memberColumns}
                refreshData={() => { refetchMembers(); }}
                isRefetching={refetchingMembers}
              />
            )}
          </CardContent>
        </Card>
      )}
      {showCreateModal && (
        <CreateModal
          open={showCreateModal}
          handleClose={() => setShowCreateModal(false)}
          title="Create Shared Data Pool"
        >
          <DataPoolCreate onSubmit={handleCreateClose} />
        </CreateModal>
      )}
      {showEditModal && selectedDataPool && (
        <DialogStandard
          open={showEditModal}
          handleClose={handleEditClose}
          title="Edit Shared Data Pool"
        >
          <DataPoolEdit onSubmit={handleEditClose} dataPool={selectedDataPool} />
        </DialogStandard>
      )}
      {showConfirmDelete && selectedDataPool && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleDelete}
          title="Delete Shared Data Pool?"
          description={`Are you certain you wish to delete "${selectedDataPool.data_pool_name}"? This action is irreversible. The pool cannot be deleted if any CPEs are still assigned to it.`}
          confirmationPhrase="confirm"
        />
      )}
    </PageContent>
  );
};
